import { ApexOptions } from "apexcharts";

type ChartOptionsParams = {
  categories: string[];
  reps: number[];
  weight: number[];
  dates: string[];
  daySplit: XAxisAnnotations[];
  feedbackPoints: PointAnnotations[];
};

export const getBaseChartOptions = ({
  categories,
  reps,
  weight,
  dates,
  daySplit,
  feedbackPoints,
  isDark = false,
}: ChartOptionsParams & { isDark?: boolean }): ApexOptions => {
  // Peso (yaxis[0])
  const weightMin = 0;
  const weightMax = Math.max(...weight);

  // Reps (yaxis[1])
  const repsMinRaw = Math.min(...reps);
  const repsMaxRaw = Math.max(...reps);
  let repsMin = repsMinRaw;
  let repsMax = repsMaxRaw;
  if (repsMinRaw === repsMaxRaw) {
    repsMin = repsMinRaw - 2;
    repsMax = repsMaxRaw + 2;
  }

  return {
    chart: {
      id: "progressionChart",
      background: "transparent",
      fontFamily: "Exo 2",
      animations: { enabled: false },
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      zoom: { enabled: true },
      foreColor: isDark ? "#fff" : "#000",
    },
    stroke: { width: [0, 3], curve: "smooth" },
    plotOptions: {
      bar: { columnWidth: "8px" },
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0],
      style: {
        fontSize: "12px",
        fontWeight: "bold",
        colors: [isDark ? "#fff" : "#000"],
      },
      background: { enabled: false },
      offsetY: -10,
      formatter: (val) => `${val}`,
    },
    yaxis: [
      {
        tickAmount: 5,
        title: {
          text: "PESO",
          style: { color: isDark ? "#fff" : "#000" },
        },
        min: weightMin,
        max: weightMax,
        labels: { style: { colors: [isDark ? "#fff" : "#000"] } },
      },
      {
        opposite: true,
        tickAmount: 5,
        title: {
          text: "REPS",
          style: { color: isDark ? "#fff" : "#000" },
        },
        min: repsMin,
        max: repsMax,
        labels: { style: { colors: [isDark ? "#fff" : "#000"] } },
      },
    ],
    xaxis: {
      categories,
      tickPlacement: "on",
      labels: {
        rotate: 0,
        style: { colors: categories.map(() => (isDark ? "#fff" : "#000")) },
      },
      axisBorder: { color: isDark ? "#fff" : "#000" },
      axisTicks: { color: isDark ? "#fff" : "#000" },
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
      x: {
        formatter: (_, { dataPointIndex }) => dates[dataPointIndex],
      },
      y: {
        formatter: (value, { seriesIndex, dataPointIndex, w }) => {
          const current = value;
          const series = w.globals.series[seriesIndex];
          const prev = series[dataPointIndex - 1];
          if (prev === undefined || isNaN(prev)) return `${current}`;
          const diff = current - prev;
          const sign = diff >= 0 ? "+" : "";
          return seriesIndex === 0
            ? `${current} (${sign}${((diff / prev) * 100).toFixed(0)}%)`
            : `${current} (${sign}${diff})`;
        },
      },
    },
    annotations: {
      xaxis: daySplit,
      points: feedbackPoints,
    },
    colors: isDark ? ["#888", "#fa0031"] : ["#b9b9b9", "#fa0031"],
    legend: {
      show: true,
      fontWeight: 600,
      offsetX: 0,
      offsetY: 5,
      labels: { colors: isDark ? "#fff" : "#000" },
    },
  };
};
